import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { getPlatformName, remoteDeleteNote, supportsRemoteDelete } from '../config/platforms';

export interface DeletablePost {
  id: string;
  status: string;
  platforms: string[];
  results?: { platform: string; status: string }[];
}

interface Props {
  post: DeletablePost | null;
  busy?: boolean;
  onCancel: () => void;
  /** Resolves true when the post was deleted, so the caller can close its detail view. */
  onConfirm: (post: DeletablePost, removeFromPlatforms: boolean) => Promise<boolean>;
}

/**
 * "Are you sure?" for deleting a post — and, for one that is already live, whether to take
 * it down from the platforms as well.
 *
 * Shared by Post History and the Calendar. The two pages previously carried their own copy
 * of this dialog with identical wording, which is exactly how the text drifts: this is the
 * one place that describes what deleting actually does.
 *
 * The distinction the dialog has to make clear is that the two halves differ in
 * reversibility. Deleting from ShoutVector is a soft delete — recoverable for 30 days.
 * Removing from a platform is permanent and cannot be undone from here, so it is opt-in and
 * spelled out rather than bundled into the same button.
 */
export const DeletePostDialog: React.FC<Props> = ({ post, busy = false, onCancel, onConfirm }) => {
  // Which platforms actually have something live to take down: only those the post
  // successfully published to. A platform it was rejected by has nothing to remove.
  const livePlatforms = (post?.results || [])
    .filter(r => r.status === 'success')
    .map(r => r.platform);

  const isLive = livePlatforms.length > 0;
  const removable = livePlatforms.filter(supportsRemoteDelete);
  const notRemovable = livePlatforms.filter(p => !supportsRemoteDelete(p));

  // Defaults to on: someone deleting a live post generally means "take this down", and the
  // checkbox is there to let them opt out rather than to make them opt in.
  const [alsoRemove, setAlsoRemove] = useState(true);

  useEffect(() => {
    if (post) setAlsoRemove(true);
  }, [post]);

  // Caveats that apply to the platforms actually involved — Instagram's pending reconnect
  // is the live case, and warning about a platform this post never reached is just noise.
  const caveats = Array.from(
    new Set(removable.map(remoteDeleteNote).filter((n): n is string => Boolean(n)))
  );

  return (
    <Dialog open={Boolean(post)} onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Delete this post?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          It will be hidden from ShoutVector but kept for 30 days, so an admin can restore it
          from Post History.
        </DialogContentText>

        {isLive && (
          <Box sx={{ mt: 2 }}>
            {removable.length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={alsoRemove}
                    disabled={busy}
                    onChange={e => setAlsoRemove(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Also remove it from {removable.map(getPlatformName).join(', ')}
                  </Typography>
                }
              />
            )}

            {alsoRemove && removable.length > 0 && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                This deletes the live post from {removable.map(getPlatformName).join(', ')}.
                That cannot be undone — restoring the post here will not put it back.
              </Alert>
            )}

            {!alsoRemove && removable.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                The post stays live on {removable.map(getPlatformName).join(', ')} and only
                disappears from ShoutVector.
              </Typography>
            )}

            {notRemovable.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {notRemovable.map(getPlatformName).join(', ')} cannot be removed through the
                API — take that copy down on the platform itself.
              </Typography>
            )}

            {caveats.map(note => (
              <Typography key={note} variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {note}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          disabled={busy}
          onClick={() => {
            if (post) onConfirm(post, alsoRemove && removable.length > 0);
          }}
        >
          {busy
            ? 'Deleting…'
            : isLive && alsoRemove && removable.length > 0
              ? 'Delete and remove'
              : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
